import React from 'react';
import { IoDocumentTextOutline, IoOpenOutline, IoArrowDownOutline } from 'react-icons/io5';
import { FileItemProps } from '../../interfaces/components/fileItem';

/**
 * FileItem component displays a file with download and open options.
 * It provides a consistent interface for displaying and interacting with downloadable files.
 *
 * @component
 * @example
 * ```tsx
 * <FileItem
 *   name="Example Document.pdf"
 *   url="https://example.com/document.pdf"
 *   onDownload={() => console.log('File downloaded')}
 *   className="custom-class"
 * />
 * ```
 *
 * @param {FileItemProps} props - The component props
 * @param {string} props.name - The display name of the file
 * @param {string} props.url - The URL where the file can be downloaded from
 * @param {Function} [props.onDownload] - Optional callback function triggered after successful download
 * @param {string} [props.className] - Optional CSS class names to be applied to the component
 *
 * @returns {JSX.Element} A file item component with download and open functionality
 */
const FileItem: React.FC<FileItemProps> = ({ name, url, onDownload, className = '' }) => {
  /**
   * Handles the file download process.
   * Creates a temporary link element to trigger the download and cleans up afterward.
   *
   * @async
   * @function handleDownload
   * @throws {Error} When the download fails
   */
  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      onDownload?.();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div
      className={`group flex items-center p-3 border-l-0 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${className}`}
    >
      <div className="flex items-center flex-1">
        <div className="w-10 h-10 mr-3 rounded-lg bg-primary/10 dark:bg-primary/5 flex items-center justify-center flex-shrink-0 border border-gray-200 dark:border-gray-700">
          <IoDocumentTextOutline size={16} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-titleText dark:text-gray-100 text-sm truncate">{name}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">PDF Document</p>
        </div>
      </div>
      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => window.open(url, '_blank')}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-primary/10 dark:hover:bg-primary/5 rounded-full transition-colors"
          title="Open in new tab"
          aria-label="Open in new tab"
        >
          <IoOpenOutline size={16} />
        </button>
        <button
          onClick={handleDownload}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-primary/10 dark:hover:bg-primary/5 rounded-full transition-colors"
          title="Download file"
          aria-label="Download file"
        >
          <IoArrowDownOutline size={16} />
        </button>
      </div>
    </div>
  );
};

export default FileItem;
